/**
 * React Query hooks for 2W Admin Dashboard
 * Provides cached, optimized API calls with loading states and error handling
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import apiClient from './api.js';

// ==========================================
// QUERY KEYS - Centralized for cache management
// ==========================================

export const QueryKeys = {
  // Authentication
  currentUser: ['auth', 'currentUser'],
  
  // Access requests
  pendingAccessRequestCount: ['access-requests', 'pending-count'],

  // Brands
  brands: ['brands'],
  brand: (id) => ['brands', id],
  brandActivity: (id) => ['brands', id, 'activity'],
  brandAnalytics: (id, params) => ['brands', id, 'analytics', params],
  brandLogistics: (id, params) => ['brands', id, 'logistics', params],
  
  // Analytics
  analyticsOverview: ['analytics', 'overview'],
  detailedAnalytics: (params) => ['analytics', 'detailed', params],
  dashboardSummary: (params) => ['dashboard', 'summary', params],
  trackingSummary: (params) => ['tracking', 'summary', params],
  trackingJobs: (params) => ['tracking', 'jobs', params],
  inventorySyncSummary: (params) => ['inventory-sync', 'summary', params],
  connectorSettings: ['connector-settings'],
  appSettings: ['app-settings'],
  connectorPerformance: (params) => ['analytics', 'connector-performance', params],
  offerImportErrorReport: (id) => ['inventory-sync', 'error-report', id],
  brandReports: (params) => ['analytics', 'brandReports', params],
  marketplaceAnalytics: (params) => ['analytics', 'marketplace', params],
  commissionSummary: (params) => ['analytics', 'commissionSummary', params],
  trendingData: (params) => ['analytics', 'trends', params],
  
  // Orders
  brandOrders: (brandId, params) => ['orders', 'brand', brandId, params],
  allOrders: (params) => ['orders', 'all', params],
  orderDetails: (orderId) => ['orders', 'details', orderId],
  orderMarketplaceBreakdown: (params) => ['orders', 'marketplace-breakdown', params],
  marketplacesList: (brandId) => ['marketplaces', 'list', brandId],
  
  // Billing
  billingReport: (brandId, year, month, options) => ['billing', brandId, year, month, options],
  
  // Marketplace Patterns - Step 1.2 Implementation
  marketplacePatterns: (params) => ['marketplace-patterns', params],
  marketplacePatternsAnalytics: (params) => ['marketplace-patterns', 'analytics', params],

  // Commission Adjustments
  commissionAdjustments: (params) => ['commission-adjustments', params],
  pendingAdjustments: ['commission-adjustments', 'pending'],
  adjustmentAuditTrail: (adjustmentId) => ['commission-adjustments', adjustmentId, 'audit'],
  adjustmentImpact: (params) => ['commission-adjustments', 'impact', params],

  // Shopify catalogue (mirrored products and variants)
  catalogueProducts: (brandId, params) => ['catalogue', brandId, 'products', params],
  catalogueVariants: (brandId, params) => ['catalogue', brandId, 'variants', params],
  catalogueStatus: (brandId) => ['catalogue', brandId, 'status'],
};

// ==========================================
// ANALYTICS HOOKS
// ==========================================

/**
 * Get system-wide analytics overview
 */
export const useAnalyticsOverview = (options = {}) => {
  return useQuery({
    queryKey: QueryKeys.analyticsOverview,
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.getAnalyticsOverview({ signal });
      } catch (error) {
        // Handle authentication errors specifically
        if (error?.status === 401 || error?.message?.includes('Authentication')) {
          await apiClient.clearToken();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - more frequent updates for overview
    gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime)
    retry: (failureCount, error) => {
      // Don't retry aborted requests or auth failures
      if (error?.message === 'Request was cancelled' || error?.status === 401) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 10 * 60 * 1000,
    refetchIntervalInBackground: false,
    ...options,
  });
};

/**
 * Get detailed analytics with filtering
 */
export const useDetailedAnalytics = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.detailedAnalytics(params),
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.getDetailedAnalytics({ ...params, signal });
      } catch (error) {
        // Handle authentication errors specifically
        if (error?.status === 401 || error?.message?.includes('Authentication')) {
          await apiClient.clearToken();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        throw error;
      }
    },
    staleTime: 90 * 1000, // 1.5 minutes - faster updates for detailed view
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      if (error?.message === 'Request was cancelled' || error?.status === 401) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    enabled: true,
    refetchOnWindowFocus: true,
    ...options,
  });
};

/**
 * Dashboard summary — connector, catalogue, sync health, logistics SLA, access requests.
 */
export const useDashboardSummary = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.dashboardSummary(params),
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.getDashboardSummary({ ...params, signal });
      } catch (error) {
        if (error?.status === 401 || error?.message?.includes('Authentication')) {
          await apiClient.clearToken();
          if (typeof window !== 'undefined') window.location.href = '/login';
        }
        throw error;
      }
    },
    staleTime: 90 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.message === 'Request was cancelled' || error?.status === 401) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: true,
    ...options,
  });
};

/**
 * Tracking — the two-step OR23/OR24 handoff, queues and shipping SLA.
 */
export const useTrackingSummary = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.trackingSummary(params),
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.getTrackingSummary({ ...params, signal });
      } catch (error) {
        if (error?.status === 401) { await apiClient.clearToken(); if (typeof window !== 'undefined') window.location.href = '/login'; }
        throw error;
      }
    },
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 2),
    refetchOnWindowFocus: true,
    ...options,
  });
};

export const useRunTrackingAction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderRowId, action }) => apiClient.runTrackingAction(orderRowId, action),
    onSettled: () => {
      // Refetch on success AND failure: a refused OR24 still writes shipValidationError,
      // which the failures table shows.
      queryClient.invalidateQueries({ queryKey: ['tracking'] });
    },
    retry: 0,
  });
};

/**
 * Fulfilment and shipping for one brand, for the brand Analytics tab.
 *
 * Shares its computation with the admin Dashboard's Logistics card, so the same brand over
 * the same window reads the same on both screens.
 */
export const useBrandLogistics = (brandId, params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.brandLogistics(brandId, params),
    queryFn: async () => apiClient.getBrandLogistics(brandId, params),
    enabled: Boolean(brandId),
    staleTime: 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 2),
    ...options,
  });
};

export const useConnectorPerformance = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.connectorPerformance(params),
    queryFn: async ({ signal }) => apiClient.getConnectorPerformance({ ...params, signal }),
    staleTime: 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 2),
    ...options,
  });
};

export const useConnectorSettings = (options = {}) => {
  return useQuery({
    queryKey: QueryKeys.connectorSettings,
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.getConnectorSettings({ signal });
      } catch (error) {
        if (error?.status === 401) { await apiClient.clearToken(); if (typeof window !== 'undefined') window.location.href = '/login'; }
        throw error;
      }
    },
    staleTime: 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 2),
    ...options,
  });
};

export const useUpdateConnectorSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch) => apiClient.updateConnectorSettings(patch),
    onSuccess: () => {
      // Refetch rather than patch the cache: the order-sweep option list depends on
      // externalPartner, so the server decides which options are still selectable.
      queryClient.invalidateQueries({ queryKey: QueryKeys.connectorSettings });
    },
    retry: 0,
  });
};

export const useAppSettings = (options = {}) => {
  return useQuery({
    queryKey: QueryKeys.appSettings,
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.getAppSettings({ signal });
      } catch (error) {
        if (error?.status === 401) { await apiClient.clearToken(); if (typeof window !== 'undefined') window.location.href = '/login'; }
        throw error;
      }
    },
    staleTime: 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 2),
    ...options,
  });
};

export const useUpdateAppSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch) => apiClient.updateAppSettings(patch),
    // Refetch rather than patch the cache: the server lower-cases and de-duplicates the
    // addresses, so what it stored is not always what was typed.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.appSettings }),
    retry: 0,
  });
};

export const useInventorySyncSummary = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.inventorySyncSummary(params),
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.getInventorySyncSummary({ ...params, signal });
      } catch (error) {
        if (error?.status === 401) { await apiClient.clearToken(); if (typeof window !== 'undefined') window.location.href = '/login'; }
        throw error;
      }
    },
    staleTime: 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 2),
    ...options,
  });
};

/** Enabled on demand — the report is only fetched when an admin opens one. */
export const useOfferImportErrorReport = (importRowId, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.offerImportErrorReport(importRowId),
    queryFn: async () => apiClient.getOfferImportErrorReport(importRowId),
    enabled: Boolean(importRowId),
    staleTime: 5 * 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 1),
    ...options,
  });
};

export const useTrackingJobs = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.trackingJobs(params),
    queryFn: async ({ signal }) => apiClient.getTrackingJobs({ ...params, signal }),
    staleTime: 60 * 1000,
    retry: (n, e) => (e?.status === 401 ? false : n < 2),
    ...options,
  });
};

/**
 * Get brand-specific reports
 */
export const useBrandReports = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.brandReports(params),
    queryFn: ({ signal }) => apiClient.getBrandReports({ ...params, signal }),
    staleTime: 5 * 60 * 1000, // 5 minutes - brand reports can be cached longer
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: (failureCount, error) => {
      if (error?.message === 'Request was cancelled') return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    ...options,
  });
};

/**
 * Get marketplace analytics
 */
export const useMarketplaceAnalytics = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.marketplaceAnalytics(params),
    queryFn: () => apiClient.getMarketplaceAnalytics(params),
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 2,
    ...options,
  });
};

/**
 * Get commission summary across brands
 */
export const useCommissionSummary = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.commissionSummary(params),
    queryFn: () => apiClient.getCommissionSummary(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    ...options,
  });
};

/**
 * Get trending data for charts
 */
export const useTrendingData = (params, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.trendingData(params),
    queryFn: () => apiClient.getTrendingData(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    enabled: !!params.metric && !!params.period,
    ...options,
  });
};

// ==========================================
// BRAND HOOKS
// ==========================================

/**
 * How many access requests are waiting on an admin.
 *
 * Feeds the badge on the sidebar's Access Requests item, which is the only thing in the
 * console that says a brand is waiting to be let in. Before it, a request sat unseen until
 * someone opened that page for an unrelated reason.
 *
 * The endpoint is a COUNT, not a page of rows — `/access-requests/pending-count` answers
 * `{ count }` off a single query, which is what makes it reasonable to ask for on every screen
 * rather than only on the one that lists them.
 *
 * refetchOnWindowFocus is left ON, unlike useBrands. A request arrives from outside the
 * console entirely — a merchant installing the app — so unlike a brand list, nothing the admin
 * does in here is what changes it, and coming back to the tab is exactly when a stale number
 * would be noticed. Approving or rejecting invalidates this key directly
 * (AccessRequests.jsx), so the badge does not wait for a refetch after the action that
 * changes it.
 */
export const usePendingAccessRequestCount = (options = {}) => {
  return useQuery({
    queryKey: QueryKeys.pendingAccessRequestCount,
    queryFn: () => apiClient.getPendingAccessRequestCount(),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    // A failed count must not retry hard or surface anything: the badge is an affordance, and
    // the page behind it works either way. It simply does not render when there is no number.
    retry: 1,
    refetchOnWindowFocus: true,
    ...options,
  });
};

/**
 * Get all brands with optional filtering, pagination, and sorting
 */
export const useBrands = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['brands', params],
    queryFn: ({ signal }) => apiClient.getBrands({ ...params, signal }),
    staleTime: 5 * 60 * 1000, // 5 minutes - allow for more frequent manual refreshes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      if (error?.message === 'Request was cancelled') return false;
      return failureCount < 3; // Retry more for brands as they're critical
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    placeholderData: (previousData) => previousData, // Keep previous data while loading
    refetchOnWindowFocus: false, // Brands don't change frequently
    ...options,
  });
};

/**
 * Get specific brand details
 */
export const useBrand = (brandId, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.brand(brandId),
    queryFn: () => apiClient.getBrand(brandId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    enabled: !!brandId,
    ...options,
  });
};

/**
 * Get brand activity log
 */
export const useBrandActivity = (brandId, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.brandActivity(brandId),
    queryFn: () => apiClient.getBrandActivity(brandId),
    staleTime: 1 * 60 * 1000, // 1 minute (activity should be fresh)
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    enabled: !!brandId,
    ...options,
  });
};


/**
 * Get brand-specific analytics data
 */
export const useBrandAnalytics = (brandId, params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.brandAnalytics(brandId, params),
    queryFn: () => apiClient.getBrandAnalytics(brandId, params),
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 2,
    enabled: !!brandId,
    ...options,
  });
};

// ==========================================
// ORDER HOOKS
// ==========================================

/**
 * Get orders for a specific brand
 */
export const useBrandOrders = (brandId, params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.brandOrders(brandId, params),
    queryFn: () => apiClient.getBrandOrders(brandId, params),
    staleTime: 0, // Always consider data stale for immediate sorting/filtering updates
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: 2,
    enabled: !!brandId,
    placeholderData: keepPreviousData, // Keep previous data while refetching to prevent empty table flash
    ...options,
  });
};

/**
 * Get orders across all brands
 */
export const useAllBrandOrders = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.allOrders(params),
    queryFn: ({ signal }) => apiClient.getAllBrandOrders({ ...params, signal }),
    staleTime: 60 * 1000, // 1 minute - orders change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      if (error?.message === 'Request was cancelled') return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: true, // Orders need fresh data
    ...options,
  });
};

/**
 * Get list of all distinct marketplaces (for filter dropdowns)
 * @param {string} brandId - Optional brand ID to filter marketplaces (use 'all' or null for all brands)
 */
export const useMarketplacesList = (brandId = 'all', options = {}) => {
  return useQuery({
    queryKey: QueryKeys.marketplacesList(brandId),
    queryFn: ({ signal }) => apiClient.getMarketplacesList({ brandId, signal }),
    staleTime: 5 * 60 * 1000, // 5 minutes - marketplaces don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      if (error?.message === 'Request was cancelled') return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: false, // Marketplaces list is relatively stable
    ...options,
  });
};

/**
 * Get marketplace breakdown for orders
 */
export const useOrderMarketplaceBreakdown = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.orderMarketplaceBreakdown(params),
    queryFn: () => apiClient.getOrderMarketplaceBreakdown(params),
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 2,
    ...options,
  });
};

// ==========================================
// BILLING HOOKS
// ==========================================

/**
 * Get billing report for a brand
 */
export const useBillingReport = (brandId, year, month, options = {}, queryOptions = {}) => {
  return useQuery({
    queryKey: QueryKeys.billingReport(brandId, year, month, options),
    queryFn: () => apiClient.getBillingReport(brandId, year, month, options),
    staleTime: 10 * 60 * 1000, // 10 minutes (billing reports are heavy)
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
    enabled: !!brandId && !!year && !!month,
    placeholderData: (previousData) => previousData,
    ...queryOptions,
  });
};

// ==========================================
// MUTATION HOOKS
// ==========================================

/**
 * Update brand configuration
 */
export const useUpdateBrand = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ brandId, data }) => apiClient.updateBrand(brandId, data),
    onSuccess: (data, { brandId }) => {
      // Invalidate and refetch brand data
      queryClient.invalidateQueries({ queryKey: QueryKeys.brand(brandId) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.brands });
      // Invalidate analytics as they might be affected
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
    retry: 1,
  });
};

/**
 * Create commission override
 */
export const useCreateCommissionOverride = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ brandId, data }) => apiClient.createCommissionOverride(brandId, data),
    onSuccess: (data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.brand(brandId) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.brandActivity(brandId) });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
    retry: 1,
  });
};

/**
 * Delete commission override
 */
export const useDeleteCommissionOverride = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ brandId, overrideId }) => apiClient.deleteCommissionOverride(brandId, overrideId),
    onSuccess: (data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.brand(brandId) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.brandActivity(brandId) });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
    retry: 1,
  });
};

/**
 * Create shipping fee
 */
export const useCreateShippingFee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ brandId, data }) => apiClient.createShippingFee(brandId, data),
    onSuccess: (data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.brand(brandId) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.brandActivity(brandId) });
    },
    retry: 1,
  });
};

/**
 * Delete shipping fee
 */
export const useDeleteShippingFee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ brandId, feeId }) => apiClient.deleteShippingFee(brandId, feeId),
    onSuccess: (data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.brand(brandId) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.brandActivity(brandId) });
    },
    retry: 1,
  });
};

/**
 * Generate billing report
 */
export const useGenerateBillingReport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ brandId, year, month }) => apiClient.generateBillingReport(brandId, year, month),
    onSuccess: (data, { brandId, year, month }) => {
      // Invalidate the specific billing report
      queryClient.invalidateQueries({ 
        queryKey: ['billing', brandId, year, month] 
      });
      // Also invalidate analytics as they might be updated
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
    retry: 1,
  });
};

/**
 * Sync orders for a brand
 */
export const useSyncOrders = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ brandId, options }) => apiClient.syncOrders(brandId, options),
    onSuccess: (data, { brandId }) => {
      // Invalidate order queries after sync
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'brand', brandId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'all'] 
      });
      // Also invalidate analytics and billing as they depend on order data
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['billing', brandId] });
    },
    retry: 1,
  });
};

// ==========================================
// UTILITY HOOKS
// ==========================================

/**
 * Prefetch data for better UX
 */
export const usePrefetchData = () => {
  const queryClient = useQueryClient();
  
  const prefetchAnalyticsOverview = () => {
    queryClient.prefetchQuery({
      queryKey: QueryKeys.analyticsOverview,
      queryFn: () => apiClient.getAnalyticsOverview(),
      staleTime: 5 * 60 * 1000,
    });
  };
  
  const prefetchBrands = () => {
    queryClient.prefetchQuery({
      queryKey: QueryKeys.brands,
      queryFn: () => apiClient.getBrands(),
      staleTime: 10 * 60 * 1000,
    });
  };
  
  return {
    prefetchAnalyticsOverview,
    prefetchBrands,
  };
};

/**
 * Force refresh data
 */
export const useRefreshData = () => {
  const queryClient = useQueryClient();
  
  const refreshAnalytics = () => {
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
  };
  
  const refreshBrands = () => {
    queryClient.invalidateQueries({ queryKey: ['brands'] });
  };
  
  const refreshAll = () => {
    queryClient.invalidateQueries();
  };
  
  const refreshMarketplacePatterns = () => {
    queryClient.invalidateQueries({ queryKey: ['marketplace-patterns'] });
  };
  
  return {
    refreshAnalytics,
    refreshBrands,
    refreshMarketplacePatterns,
    refreshAll,
  };
};

// ==========================================
// MARKETPLACE PATTERNS HOOKS - Step 1.2 Implementation
// ==========================================

/**
 * Get marketplace patterns with pagination and filtering
 */
export const useMarketplacePatterns = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.marketplacePatterns(params),
    queryFn: () => apiClient.getMarketplacePatterns(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    placeholderData: (previousData) => previousData, // Keep previous data while loading new page
    ...options,
  });
};

/**
 * Get marketplace patterns analytics
 */
export const useMarketplacePatternsAnalytics = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.marketplacePatternsAnalytics(params),
    queryFn: () => apiClient.getMarketplacePatternsAnalytics(params),
    staleTime: 10 * 60 * 1000, // 10 minutes (analytics can be less fresh)
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    ...options,
  });
};

/**
 * Create marketplace pattern
 */
export const useCreateMarketplacePattern = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (patternData) => apiClient.createMarketplacePattern(patternData),
    onSuccess: () => {
      // Invalidate and refetch patterns data
      queryClient.invalidateQueries({ queryKey: ['marketplace-patterns'] });
    },
    retry: 1,
  });
};

/**
 * Update marketplace pattern
 */
export const useUpdateMarketplacePattern = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, ...data }) => apiClient.updateMarketplacePattern(id, data),
    onSuccess: () => {
      // Invalidate patterns data to refetch updated information
      queryClient.invalidateQueries({ queryKey: ['marketplace-patterns'] });
    },
    retry: 1,
  });
};

/**
 * Delete marketplace pattern
 */
export const useDeleteMarketplacePattern = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (patternId) => apiClient.deleteMarketplacePattern(patternId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-patterns'] });
    },
    // Refresh on failure TOO. The common failure is a 404 — the pattern is already gone and
    // the list on screen is stale. Without this the phantom row stayed, so the obvious next
    // move was to click delete again, which failed again.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-patterns'] });
    },
    // No retry. A delete that failed because the record does not exist cannot succeed on a
    // second attempt, and retrying logged the same server error twice for one click — which is
    // how a single stale row produced a duplicated stack trace in the backend log.
    retry: false,
  });
};

/**
 * Test marketplace pattern (mutation for testing patterns)
 */
export const useTestMarketplacePattern = () => {
  return useMutation({
    mutationFn: (...args) => apiClient.testMarketplacePattern(...args),
    retry: 1,
  });
};

// ==========================================
// SHIPPING RULES HOOKS
// ==========================================

/**
 * useShippingRules removed - ShippingRule system deprecated
 * Use ShippingPricingTable component and /shipping-pricing endpoint instead
 */

// ==========================================
// COMMISSION ADJUSTMENT HOOKS
// ==========================================

/**
 * Get commission adjustments with filtering and pagination
 */
export const useCommissionAdjustments = (params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.commissionAdjustments(params),
    queryFn: ({ signal }) => {
      return apiClient.getCommissionAdjustments({ ...params, signal });
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    placeholderData: (previousData) => previousData,
    ...options,
  });
};

/**
 * Get pending commission adjustments for approval
 */
export const usePendingAdjustments = (options = {}) => {
  return useQuery({
    queryKey: QueryKeys.pendingAdjustments,
    queryFn: ({ signal }) => {
      return apiClient.getPendingAdjustments({ signal });
    },
    staleTime: 1 * 60 * 1000, // 1 minute - more frequent for approval workflow
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    ...options,
  });
};

/**
 * Get audit trail for a commission adjustment
 */
export const useAdjustmentAuditTrail = (adjustmentId, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.adjustmentAuditTrail(adjustmentId),
    queryFn: ({ signal }) => {
      return apiClient.getAdjustmentAuditTrail(adjustmentId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: 1,
    enabled: !!adjustmentId,
    ...options,
  });
};

/**
 * Create commission adjustment mutation
 */
export const useCreateCommissionAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (adjustmentData) => apiClient.createCommissionAdjustment(adjustmentData),
    onSuccess: () => {
      // Invalidate all commission adjustment queries
      queryClient.invalidateQueries({ queryKey: ['commission-adjustments'] });
    },
    retry: 1,
  });
};

/**
 * Update commission adjustment mutation
 */
export const useUpdateCommissionAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adjustmentId, updateData }) =>
      apiClient.updateCommissionAdjustment(adjustmentId, updateData),
    onSuccess: (data, variables) => {
      // Invalidate all commission adjustment queries
      queryClient.invalidateQueries({ queryKey: ['commission-adjustments'] });
      // Invalidate specific adjustment audit trail
      queryClient.invalidateQueries({
        queryKey: QueryKeys.adjustmentAuditTrail(variables.adjustmentId)
      });
    },
    retry: 1,
  });
};

/**
 * Approve commission adjustment mutation
 */
export const useApproveCommissionAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (adjustmentId) => apiClient.approveCommissionAdjustment(adjustmentId),
    onSuccess: (data, adjustmentId) => {
      // Invalidate all commission adjustment queries
      queryClient.invalidateQueries({ queryKey: ['commission-adjustments'] });
      // Invalidate pending adjustments specifically
      queryClient.invalidateQueries({ queryKey: QueryKeys.pendingAdjustments });
      // Invalidate audit trail
      queryClient.invalidateQueries({
        queryKey: QueryKeys.adjustmentAuditTrail(adjustmentId)
      });
    },
    retry: 1,
  });
};

/**
 * Reject commission adjustment mutation
 */
export const useRejectCommissionAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adjustmentId, reason }) => apiClient.rejectCommissionAdjustment(adjustmentId, reason),
    onSuccess: (data, variables) => {
      // Invalidate all commission adjustment queries
      queryClient.invalidateQueries({ queryKey: ['commission-adjustments'] });
      // Invalidate pending adjustments specifically
      queryClient.invalidateQueries({ queryKey: QueryKeys.pendingAdjustments });
      // Invalidate audit trail
      queryClient.invalidateQueries({
        queryKey: QueryKeys.adjustmentAuditTrail(variables.adjustmentId)
      });
    },
    retry: 1,
  });
};

/**
 * Delete commission adjustment mutation
 */
export const useDeleteCommissionAdjustment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adjustmentId, reason }) =>
      apiClient.deleteCommissionAdjustment(adjustmentId, reason),
    onSuccess: (data, variables) => {
      // Invalidate all commission adjustment queries
      queryClient.invalidateQueries({ queryKey: ['commission-adjustments'] });
      // Invalidate audit trail
      queryClient.invalidateQueries({
        queryKey: QueryKeys.adjustmentAuditTrail(variables.adjustmentId)
      });
    },
    retry: 1,
  });
};

/**
 * Calculate adjustment impact
 */
export const useCalculateAdjustmentImpact = () => {
  return useMutation({
    mutationFn: (impactData) => apiClient.calculateAdjustmentImpact(impactData),
    retry: 1,
  });
};
// ==========================================
// SHOPIFY CATALOGUE HOOKS
// ==========================================

/**
 * staleTime is 5 minutes, not 0. The catalogue is re-mirrored once a DAY
 * (sync-scheduler.js registers shopify-catalogue-<brandId> at 03:00 + a per-brand stagger),
 * so treating it as immediately stale — the convention for orders, where sorting and
 * filtering happen server-side against changing data — would refetch constantly for data
 * that cannot have moved.
 *
 * placeholderData keeps the previous page on screen while the next one loads, so paging does
 * not flash an empty table.
 */
export const useCatalogueProducts = (brandId, params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.catalogueProducts(brandId, params),
    queryFn: () => apiClient.getCatalogueProducts(brandId, params),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
    enabled: !!brandId,
    placeholderData: keepPreviousData,
    ...options,
  });
};

/** Variants of one product, or of the whole brand when no productId is passed. */
export const useCatalogueVariants = (brandId, params = {}, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.catalogueVariants(brandId, params),
    queryFn: () => apiClient.getCatalogueVariants(brandId, params),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 2,
    enabled: !!brandId,
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const useCatalogueStatus = (brandId, options = {}) => {
  return useQuery({
    queryKey: QueryKeys.catalogueStatus(brandId),
    queryFn: () => apiClient.getCatalogueStatus(brandId),
    staleTime: 60 * 1000,
    retry: 2,
    enabled: !!brandId,
    ...options,
  });
};
